import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getTestimonials } from '../api/products';
import client from '../api/client';

interface Testimonial {
    id: number;
    name: string;
    role?: string;
    content?: string;
    content_ar?: string;
    content_fr?: string;
    rating: number;
    avatar?: string;
    image?: string;
    audio?: string;
}

interface Props {
    productId?: number;
}

const TestimonialsCarousel: React.FC<Props> = ({ productId }) => {
    const { t, i18n } = useTranslation();
    const isRTL = i18n.dir() === 'rtl';
    const [testimonials, setTestimonials] = useState<Testimonial[]>([]);
    const [loading, setLoading] = useState(true);
    const [currentIndex, setCurrentIndex] = useState(0);

    useEffect(() => {
        const fetchTestimonials = async () => {
            try {
                const data = await getTestimonials(productId);
                setTestimonials(data);
            } catch (error) {
                console.error('Failed to fetch testimonials', error);
                setTestimonials([
                    { id: 1, name: 'Ahmed Benali', role: 'Verified Customer', content: 'Excellent quality!', content_ar: 'جودة ممتازة!', rating: 5 },
                    { id: 2, name: 'Sarah Amrani', role: 'Loyal Client', content: 'Amazing product!', content_ar: 'منتج رائع!', rating: 5 },
                    { id: 3, name: 'Karim Tazi', role: 'Verified Customer', content: 'Will order again.', content_ar: 'سأطلب مجدداً.', rating: 4 }
                ]);
            } finally {
                setLoading(false);
            }
        };
        fetchTestimonials();
    }, [productId]);

    const goNext = () => setCurrentIndex(prev => (prev + 1) % testimonials.length);
    const goPrev = () => setCurrentIndex(prev => (prev - 1 + testimonials.length) % testimonials.length);

    if (loading || testimonials.length === 0) return null;

    const testimonial = testimonials[currentIndex];

    const btnStyle: React.CSSProperties = {
        position: 'absolute',
        top: '50%',
        transform: 'translateY(-50%)',
        zIndex: 10,
        width: 36,
        height: 36,
        borderRadius: '50%',
        background: 'rgba(255,255,255,0.95)',
        border: '1px solid #e5e7eb',
        boxShadow: '0 4px 15px rgba(0,0,0,0.15)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        cursor: 'pointer',
        color: '#374151',
        padding: 0,
    };

    return (
        <section style={{ padding: '80px 0', background: '#fff' }}>
            {/* Force LTR on the outer wrapper so RTL page direction never flips our layout */}
            <div style={{ maxWidth: 800, margin: '0 auto', padding: '0 24px', direction: 'ltr' }}>
                <div style={{ textAlign: isRTL ? 'right' : 'center', marginBottom: 48, direction: isRTL ? 'rtl' : 'ltr' }}>
                    <span style={{ color: '#059669', fontWeight: 600, textTransform: 'uppercase', fontSize: 14, letterSpacing: '0.1em' }}>
                        {t('testimonials.subtitle', 'What Our Clients Say')}
                    </span>
                    <h2 style={{ fontSize: 32, fontWeight: 700, color: '#111827', marginTop: 8 }}>
                        {t('testimonials.title', 'Customer Testimonials')}
                    </h2>
                </div>

                {/* Outer: relative so absolute buttons are anchored to it. Desktop: padding for buttons outside card. Mobile: 0 padding, buttons overlap card edges */}
                <div className="relative px-0 md:px-14">

                    {/* LEFT ARROW — always < icon, always on physical left, only onClick changes for RTL */}
                    <button
                        onClick={isRTL ? goNext : goPrev}
                        style={{ ...btnStyle, left: -4 }}
                        aria-label='Previous'
                    >
                        <svg width='16' height='16' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                            <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2.5} d='M15 19l-7-7 7-7' />
                        </svg>
                    </button>

                    {/* CARD — always 100% width of this container */}
                    <div style={{ width: '100%', background: '#f9fafb', borderRadius: 20, border: '1px solid #f3f4f6', padding: 32, boxShadow: '0 2px 20px rgba(0,0,0,0.06)', direction: isRTL ? 'rtl' : 'ltr', textAlign: isRTL ? 'right' : 'left', boxSizing: 'border-box' }}>
                        <div style={{ display: 'flex', gap: 4, marginBottom: 16, justifyContent: isRTL ? 'flex-end' : 'flex-start' }}>
                            {[...Array(5)].map((_, i) => (
                                <svg key={i} width='22' height='22' fill={i < testimonial.rating ? '#FBBF24' : '#D1D5DB'} viewBox='0 0 20 20'>
                                    <path d='M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z' />
                                </svg>
                            ))}
                        </div>

                        {testimonial.audio && (
                            <div style={{ marginBottom: 16 }}>
                                <audio controls style={{ width: '100%' }} controlsList='nodownload'>
                                    <source src={`${client.defaults.baseURL?.replace('/api', '')}/storage/${testimonial.audio}`} type='audio/mpeg' />
                                </audio>
                            </div>
                        )}

                        <p style={{ color: '#4B5563', fontStyle: 'italic', marginBottom: 24, fontSize: 16, lineHeight: 1.7 }}>
                            "{isRTL ? (testimonial.content_ar || testimonial.content) : (testimonial.content_fr || testimonial.content)}"
                        </p>

                        <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexDirection: isRTL ? 'row-reverse' : 'row' }}>
                            <div style={{ width: 48, height: 48, borderRadius: '50%', background: '#e6f4f1', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: 18, color: '#059669', flexShrink: 0, overflow: 'hidden' }}>
                                {testimonial.image ? (
                                    <img src={`${client.defaults.baseURL?.replace('/api', '')}/storage/${testimonial.image}`} alt={testimonial.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                ) : testimonial.name.charAt(0)}
                            </div>
                            <div>
                                <h4 style={{ fontWeight: 700, color: '#111827', margin: 0 }}>{testimonial.name}</h4>
                                <p style={{ fontSize: 14, color: '#6B7280', margin: 0 }}>{testimonial.role}</p>
                            </div>
                        </div>
                    </div>

                    {/* RIGHT ARROW — always > icon, always on physical right, only onClick changes for RTL */}
                    <button
                        onClick={isRTL ? goPrev : goNext}
                        style={{ ...btnStyle, right: -4 }}
                        aria-label='Next'
                    >
                        <svg width='16' height='16' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                            <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2.5} d='M9 5l7 7-7 7' />
                        </svg>
                    </button>
                </div>

                <div style={{ display: 'flex', justifyContent: 'center', gap: 8, marginTop: 24 }}>
                    {testimonials.map((_, i) => (
                        <button key={i} onClick={() => setCurrentIndex(i)} style={{ width: i === currentIndex ? 28 : 8, height: 8, borderRadius: 999, background: i === currentIndex ? '#059669' : '#D1D5DB', border: 'none', cursor: 'pointer', transition: 'all 0.3s ease', padding: 0 }} aria-label={`Go to testimonial ${i + 1}`} />
                    ))}
                </div>
            </div>
        </section>
    );
};

export default TestimonialsCarousel;
